import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Divider, useTheme } from "@mui/material";
import { useRecommendationDetails } from "../../../RecommendationDetailsProvider";
import { TwoColLayout } from "../TwoColLayout";
import { BreakdownBlock } from "./components/BreakdownBlock";
import { BreakdownDetails, ItemGroup } from "./components/BreakdownDetails";
import { ShowMoreTypography } from "./components/ShowMoreTypography";
import { getBreakdownDetails, getBreakdownChartData } from "./utils";

export const RegionSection = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isGerman = i18n.language === "de";
  const { investment, fundDetails, investmentFundDetails, logos, names } =
    useRecommendationDetails();
  const [showMore, setShowMore] = useState(false);
  const [hoveredId, setHoveredId] = useState<string>();

  const breakdown = useMemo(
    () => fundDetails?.breakdowns?.country || [],
    [fundDetails?.breakdowns?.country]
  );
  const investmentBreakdown = useMemo(
    () => investmentFundDetails?.breakdowns?.country || [],
    [investmentFundDetails?.breakdowns?.country]
  );

  const chartData = useMemo(
    () => getBreakdownChartData(breakdown, theme, isGerman),
    [breakdown, theme, isGerman]
  );

  const chartDataInvestment = useMemo(
    () => getBreakdownChartData(investmentBreakdown, theme, isGerman),
    [investmentBreakdown, theme, isGerman]
  );

  const formattedItems: ItemGroup[] = useMemo(
    () =>
      getBreakdownDetails({
        breakdown,
        investmentBreakdown,
        logos,
        isGerman,
      }),
    [breakdown, investmentBreakdown, logos, isGerman]
  );

  const total = investment?.currentMarketValueAC ?? 0;

  if (breakdown.length === 0 && investmentBreakdown.length === 0) {
    return null;
  }

  return (
    <Box>
      <TwoColLayout
        title={t("recommender.compareView.donutCharts.regionTitle")}
        marginBottom={6}
        component1={
          <BreakdownBlock
            data={chartDataInvestment}
            name={names.investment}
            logo={logos.originalFund}
            total={total}
            hoveredId={hoveredId}
            setHoveredId={setHoveredId}
          />
        }
        component2={
          <BreakdownBlock
            data={chartData}
            name={names.recommendation}
            logo={logos.recommendation}
            total={total}
            hoveredId={hoveredId}
            setHoveredId={setHoveredId}
          />
        }
      />
      {!showMore && (
        <ShowMoreTypography showMore={showMore} setShowMore={setShowMore} />
      )}
      <BreakdownDetails
        title={t("recommender.compareView.donutCharts.region")}
        showMore={showMore}
        investmentName={names.investment}
        recommendationName={names.recommendation}
        items={formattedItems}
      />
      {showMore && (
        <ShowMoreTypography
          showMore={showMore}
          setShowMore={setShowMore}
          marginTop={8}
        />
      )}
      <Divider sx={{ mt: 9, mb: 8 }} />
    </Box>
  );
};
